import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Bell, BellOff, Volume2, VolumeX, Wallet, Building2 } from "lucide-react";

const LABEL = {
  pre_foreclosure: "Pre-Foreclosure",
  post_foreclosure_surplus: "Surplus Funds",
  sell_fast: "Sell Fast",
  agent_partner: "Agent Partner",
  probate_attorney: "Probate Attorney",
  property_manager: "Property Manager",
  contractor: "Contractor",
  other: "Other",
};

const POLL_MS = 7000;
const STORAGE_SEEN_KEY = "hrs_admin_seen_lead_ids";

// Short, pleasant chime (base64 WAV, ~0.4s)
const CHIME_SRC =
  "data:audio/wav;base64,UklGRuQGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YcAGAAAAAAIAAwABAAIAAQACAAMAAQADAAEAAgACAAEAAQABAAEAAwACAAMAAwABAAMAAgACAAEAAwACAAIAAwADAAEAAQACAAMAAwACAAIAAwABAAEAAQADAAEAAwABAAIAAQADAAMAAwACAAIAAwABAAIAAwABAAEAAQACAAIAAwABAAEAAwACAAMAAgADAAEAAQACAAMAAwABAAMAAgADAAEAAQABAAMAAgACAAIAAwABAAEAAwACAAMAAQABAAIAAgABAAMAAwACAAIAAgACAAEAAwACAAIAAwABAAIAAwABAAIAAgACAAEAAQACAAIAAwADAAIAAwACAAEAAwACAAMAAwACAAMAAQABAAEAAgADAAEAAwADAAMAAgADAAIAAwACAAIAAgADAAMAAwABAAEAAgABAAEAAQABAAMAAgACAAIAAgADAAEAAwABAAIAAgABAAIAAwABAAMAAgADAAIAAgADAAMAAgADAAEAAgABAAEAAgACAAIAAwABAAEAAQACAAMAAwABAAEAAgACAAEAAQAAAAEAAwACAAIAAwACAAEAAgABAAMAAwADAAEAAgADAAEAAgACAAMAAgABAAEAAgACAAMAAwABAAIAAgACAAMAAwABAAEAAgACAAEAAgABAAEAAgABAAEAAgABAAMAAgABAAIAAwACAAMAAgADAAEAAgADAAIAAgACAAEAAQACAAIAAgABAAIAAwADAAEAAwACAAEAAQACAAEAAQADAAMAAQADAAIAAgADAAEAAgADAAIAAgACAAEAAQABAAMAAwACAAIAAwACAAIAAQADAAMAAwACAAMAAwADAAEAAwABAAEAAQABAAIAAQACAAIAAgACAAIAAgABAAMAAQACAAIAAgACAAEAAQADAAIAAgADAAIAAQABAAEAAwABAAEAAQACAAMAAQACAAMAAQADAAMAAwACAAEAAQACAAEAAwACAAMAAgABAAIAAQACAAEAAgACAAEAAwABAAMAAQADAAIAAwADAAEAAgACAAIAAgACAAEAAgADAAMAAQACAAIAAQACAAEAAQABAAMAAgABAAIAAwADAAMAAwACAAMAAwABAAIAAQABAAIAAgABAAEAAwABAAEAAQABAAIAAwABAAEAAQADAAIAAwABAAMAAQADAAEAAwABAAMAAwABAAMAAQACAAMAAgABAAMAAwADAAMAAwADAAEAAQABAAEAAQACAAIAAgACAAMAAgADAAMAAwACAAMAAwADAAEAAwACAAIAAQACAAIAAgABAAIAAQADAAEAAQACAAMAAgABAAIAAwACAAEAAwABAAIAAwACAAMAAgACAAMAAgADAAEAAwADAAEAAgADAAIAAQACAAEAAgACAAIAAwACAAMAAwACAAEAAwACAAIAAgAB";

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [flashIds, setFlashIds] = useState(new Set());
  const [unread, setUnread] = useState(0);

  const seenRef = useRef(new Set());
  const audioRef = useRef(null);
  const firstLoadRef = useRef(true);
  const titleDefault = "Admin · Lead Dashboard";

  // Load seen ids from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_SEEN_KEY);
      if (raw) seenRef.current = new Set(JSON.parse(raw));
    } catch {}
  }, []);

  const persistSeen = () => {
    try {
      localStorage.setItem(
        STORAGE_SEEN_KEY,
        JSON.stringify(Array.from(seenRef.current))
      );
    } catch {}
  };

  const playChime = () => {
    if (!soundOn) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(CHIME_SRC);
        audioRef.current.volume = 0.5;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch {}
  };

  const notifyBrowser = (lead) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    try {
      const n = new Notification("New lead — Clear Path Home Relief Services", {
        body: `${lead.full_name} · ${lead.phone} · ${LABEL[lead.situation] || lead.situation}`,
        tag: lead.id,
        icon: "/favicon.ico",
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {}
  };

  const askPermission = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
    } catch {}
  };

  const load = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
      const newLeads = l.data;

      // Detect brand-new leads (not in seenRef)
      if (!firstLoadRef.current) {
        const fresh = newLeads.filter((x) => !seenRef.current.has(x.id));
        if (fresh.length > 0) {
          playChime();
          fresh.slice(0, 3).forEach(notifyBrowser);
          setUnread((u) => u + fresh.length);
          setFlashIds((prev) => {
            const next = new Set(prev);
            fresh.forEach((f) => next.add(f.id));
            return next;
          });
          // Clear flash highlighting after 6s
          setTimeout(() => {
            setFlashIds((prev) => {
              const next = new Set(prev);
              fresh.forEach((f) => next.delete(f.id));
              return next;
            });
          }, 6000);
        }
      }

      // Mark everything as seen
      newLeads.forEach((x) => seenRef.current.add(x.id));
      persistSeen();
      firstLoadRef.current = false;

      setLeads(newLeads);
      setStats(s.data);
      setErr(null);
    } catch (e) {
      setErr(e?.message || "Failed to load leads");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Initial load + polling
  useEffect(() => {
    load(false);
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update page title with unread
  useEffect(() => {
    document.title = unread > 0 ? `(${unread}) New Leads · Clear Path` : titleDefault;
  }, [unread]);

  // Clear unread when user returns focus
  useEffect(() => {
    const onFocus = () => setUnread(0);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#2C5545]">
              Clear Path Home Relief Services
            </div>
            <h1 className="font-display text-3xl lg:text-4xl text-[#0B1B3D]">
              Lead Dashboard
              {unread > 0 && (
                <span
                  data-testid="admin-unread-badge"
                  className="ml-3 inline-block align-middle text-sm bg-[#C5A059] text-[#0B1B3D] rounded-full px-3 py-1 font-sans font-semibold animate-pulse"
                >
                  {unread} new
                </span>
              )}
            </h1>
            <p className="text-xs text-[#64748B] mt-1">
              Live — auto-refreshes every {POLL_MS / 1000}s · Keep this tab open for instant alerts
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/admin/deals"
              data-testid="admin-go-to-deals"
              className="px-3 py-2 text-sm rounded-md bg-[#2C5545] text-white hover:bg-[#3A6E5A] flex items-center gap-2"
            >
              <Building2 className="h-4 w-4" />
              Wholesale Deals
            </Link>
            <Link
              to="/admin/cash-buyers"
              data-testid="admin-go-to-buyers"
              className="px-3 py-2 text-sm rounded-md bg-[#0B1B3D] text-white hover:bg-[#1A2F5C] flex items-center gap-2"
            >
              <Wallet className="h-4 w-4" />
              Cash Buyers
            </Link>
            <button
              onClick={() => setSoundOn((v) => !v)}
              data-testid="admin-sound-toggle"
              title={soundOn ? "Mute sound" : "Enable sound"}
              className="px-3 py-2 text-sm rounded-md border border-[#0B1B3D]/30 text-[#0B1B3D] hover:bg-white flex items-center gap-2"
            >
              {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              {soundOn ? "Sound on" : "Sound off"}
            </button>

            {notifPerm !== "granted" ? (
              <button
                onClick={askPermission}
                data-testid="admin-enable-notifications"
                className="px-3 py-2 text-sm rounded-md bg-[#C5A059] text-[#0B1B3D] hover:bg-[#B38F4E] font-medium flex items-center gap-2"
              >
                <Bell className="h-4 w-4" />
                Enable desktop alerts
              </button>
            ) : (
              <span className="px-3 py-2 text-sm rounded-md bg-[#2C5545]/10 text-[#2C5545] flex items-center gap-2">
                <Bell className="h-4 w-4" /> Alerts on
              </span>
            )}

            <button
              onClick={() => load(false)}
              className="px-4 py-2 text-sm rounded-md border border-[#0B1B3D] text-[#0B1B3D] hover:bg-white"
              data-testid="admin-refresh"
            >
              Refresh
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <StatCard k={stats.total} v="Total" />
            <StatCard k={stats.pre_foreclosure} v="Pre-Foreclosure" />
            <StatCard k={stats.post_foreclosure_surplus} v="Surplus Funds" />
            <StatCard k={stats.sell_fast} v="Sell Fast" />
            <StatCard k={stats.agent_partner} v="Agents" />
            <StatCard k={stats.probate_attorney} v="Probate Attys" />
            <StatCard k={stats.property_manager} v="Prop Managers" />
            <StatCard k={stats.contractor} v="Contractors" />
            <StatCard k={stats.other} v="Other" />
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-[#64748B]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading leads...
          </div>
        ) : err ? (
          <div className="text-red-700">{err}</div>
        ) : leads.length === 0 ? (
          <div className="rounded-lg border border-[#E5E3DB] bg-white p-10 text-center text-[#64748B]">
            No leads yet. Submitted inquiries will appear here instantly.
          </div>
        ) : (
          <div className="rounded-lg border border-[#E5E3DB] bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-[#F3F2EE] text-[#0B1B3D] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Phone</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Situation</th>
                  <th className="text-left p-3">Address</th>
                  <th className="text-left p-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const isNew = flashIds.has(l.id);
                  return (
                    <tr
                      key={l.id}
                      data-testid={`admin-lead-row-${l.id}`}
                      className={`border-t border-[#E5E3DB] transition-colors ${
                        isNew ? "bg-[#C5A059]/15" : ""
                      }`}
                    >
                      <td className="p-3 whitespace-nowrap text-[#64748B]">
                        {isNew && (
                          <span className="inline-block mr-2 text-[10px] uppercase tracking-wider bg-[#C5A059] text-[#0B1B3D] rounded-full px-2 py-0.5 font-semibold">
                            New
                          </span>
                        )}
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                      <td className="p-3 font-medium text-[#0B1B3D]">{l.full_name}</td>
                      <td className="p-3">
                        <a href={`tel:${l.phone}`} className="text-[#0B1B3D] hover:text-[#C5A059]">
                          {l.phone}
                        </a>
                      </td>
                      <td className="p-3">
                        {l.email ? (
                          <a href={`mailto:${l.email}`} className="text-[#0B1B3D] hover:text-[#C5A059]">
                            {l.email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="p-3">
                        <span className="inline-block text-[11px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#2C5545]/10 text-[#2C5545]">
                          {LABEL[l.situation] || l.situation}
                        </span>
                      </td>
                      <td className="p-3 text-[#64748B]">{l.property_address || "—"}</td>
                      <td className="p-3 text-[#64748B] text-xs">
                        {[
                          l.loan_balance && `Loan: ${l.loan_balance}`,
                          l.estimated_value && `Value: ${l.estimated_value}`,
                          l.timeline && `Timeline: ${l.timeline}`,
                          l.message && `"${l.message}"`,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}

function StatCard({ k, v }) {
  return (
    <div className="rounded-lg bg-white border border-[#E5E3DB] p-5">
      <div className="font-display text-3xl text-[#0B1B3D] leading-none">{k}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-[#64748B] mt-2">{v}</div>
    </div>
  );
}
